import React, { useCallback, useEffect, useRef } from 'react';
import { useThree, useFrame, useLoader } from '@react-three/fiber';
import { Vector3, TextureLoader, LinearEncoding, FrontSide, Color } from 'three';

import poiOpacityMap from 'assets/icons/poiOpacityMap.jpg';
import { useAnalyticsData } from 'hooks/useAnalyticsData';
import { useStore, useMItem } from 'store';
import { build_dijkstra } from 'utils/three_helpers';
import { sendAnalyticsData } from 'utils/handleAnalytics';

type POIProps = {
  board: any;
  point_index: number;
};

export const POI = ({ board, point_index }: POIProps) => {
  const camera = useThree((state) => state.camera);
  const active_board = useStore((state) => state.scene_controls.active_board);
  const billboard_listener_click = useStore((state) => state.billboard_settings.billboard_listener_click);
  const camera_density = useStore((state) => state.billboard_settings.camera_density);
  const fps = useStore((state) => state.fps);
  const pulse = useStore((state) => state.poiPulse);

  const { billboard_list = [] } = useMItem() || {};
  const analyticsData = useAnalyticsData({ event_type: `poi_click_${point_index}` });
  const ball = useRef<any>();

  const density = camera_density / (60 / fps);

  const emissiveColor = new Color('#63666A');

  const clickBall = useCallback(
    async (event?: any) => {
      if (event) event.stopPropagation();
      // console.log('point_index')
      if (point_index === 0 && event) return; // Don't allow click of center position ball 1st ball (breaks)

      const { lattice_graph, clipAnchors, lattice, orbit_controls_target } = useStore.getState().billboard_settings;
      const cameraPosition = new Vector3(camera.position.x, camera.position.y, camera.position.z);
      const locatorPosition = new Vector3(
        billboard_list[point_index].poi.locator.position.x,
        billboard_list[point_index].poi.locator.position.y,
        billboard_list[point_index].poi.locator.position.z,
      );

      const { points, targetPositions } = build_dijkstra(
        lattice_graph,
        lattice,
        clipAnchors,
        orbit_controls_target,
        cameraPosition,
        locatorPosition,
        density,
        point_index,
      );

      useStore.setState((state) => ({
        billboard_settings: {
          ...state.billboard_settings,
          current_camera_keyframe: 0,
          camera_move_positions: points,
          target_positions: targetPositions,
          is_camera_keyframe: true,
          billboard_listener_click: false,
        },
        scene_controls: {
          ...state.scene_controls,
          active_board: point_index,
          active_board_changed: true,
        },
      }));
      // ANALYTICS TODO
      await sendAnalyticsData(analyticsData);
    },
    // eslint-disable-next-line
    [point_index, camera, billboard_list, density],
  );

  // If active board and point_index match then call clickBall fn, For outside active board change listener
  useEffect(() => {
    if (!billboard_listener_click) return;
    if (active_board === point_index) clickBall();
  }, [active_board, point_index, clickBall, billboard_listener_click]);

  useFrame(() => {
    ball.current.lookAt(camera.position.x, camera.position.y, camera.position.z);
  });

  const _handleVisiblePOI = () => {
    if (point_index === active_board) return false; // Hide If currenlty active
    if (point_index === 0) return false; // Defualt Position for POI don't allow POI Click

    return true;
  };

  return (
    <group
      name={`poi_${point_index}`}
      dispose={null}
      onClick={(event) => clickBall(event)}
      visible={_handleVisiblePOI()}
    >
      <mesh ref={ball} name={'ball_one'} position={[board.poi.position.x, board.poi.position.y, board.poi.position.z]}>
        <planeGeometry args={[board.poi.size * 1.2 * pulse, board.poi.size * 1.2 * pulse]} attach="geometry" />

        <meshStandardMaterial
          transparent={true}
          side={FrontSide}
          attach="material"
          opacity={1}
          roughness={1}
          metalness={0}
          color={'black'}
          emissive={emissiveColor}
          emissiveIntensity={1}
        >
          <Opacity />
        </meshStandardMaterial>
      </mesh>
    </group>
  );
};

const Opacity = () => {
  const texture = useLoader(TextureLoader, poiOpacityMap);

  return <texture attach="alphaMap" {...texture} encoding={LinearEncoding} name="updated_levar_opacity" />;
};
